import {
  LitElement,
  html,
  broadcastReceiver,
  css,
  t,
  unsafeHTML,
  paths,
} from '@hh/global.js'

class HHGridFadeInItems extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.js-grid-fade-in')]
    const skip_to_content = document.querySelector(
      '.js-skip-to-content-link-lookbook'
    )

    const bodyWidth = document.body.clientWidth
    if (bodyWidth < 750) {
      this.items.forEach((item) => {
        item.classList.remove('lookbook__look--will-fade')
        item.querySelector('.lookbook__content').classList.remove('hidden')
      })
      return
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.init(entry.target)
          }
        })
      },
      { threshold: 0.2 }
    )

    this.items.forEach((item) => {
      this.observer.observe(item)
    })

    skip_to_content.addEventListener('click', () => {
      this.items.forEach((item) => {
        this.init(item)
      })
    })
  }

  disconnectedCallback() {
    this.observer.disconnect()
  }

  init(item) {
    item.querySelector('.lookbook__content').classList.remove('hidden')

    setTimeout(() => {
      item.querySelector('.lookbook__content').classList.add('fadein')
    }, 15)
  }
}

customElements.define('hh-grid-fade-in-items', HHGridFadeInItems)

function findVideoSrc(srcs) {
  let src = srcs.find((src) => src.height == 1080)?.url
  if (!src) {
    src = srcs[0].url
  }
  return src
}

function handlePlay() {
  const video = this.shadowRoot.querySelector('video')
  if (video.paused) {
    video.play()
    video.classList.add('is-playing')
  } else {
    video.pause()
    video.classList.remove('is-playing')
  }
}

// In a trunkshow modal, the media and title are rendered using template strings
// The product grid is rendered by fetching the product-card template and inserting it into the shadow dom of the trunkshow content
class Trunkshow extends LitElement {
  static properties = {
    title: { type: String },
    mediaDesktop: { type: String },
    mediaMobile: { type: String },
    products: { type: Array },
    BISPopovers: { type: Object },
  }

  constructor() {
    super()

    this.reset = this.reset.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.renderProducts = this.renderProducts.bind(this)
    this.receiver = broadcastReceiver('trunkshow', (e) => {
      this.requestUpdate()
      this.init(e)
    })
    this.BISPopovers = {}

    window.addEventListener('trunkshow-closed', this.reset)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver && this.receiver.disconnect()

    this.reset()
    window.removeEventListener('trunkshow-closed', this.reset)
  }

  reset() {
    this.shadowRoot.querySelector('.wrapper')?.classList.remove('fade-in')

    this.shadowRoot
      .querySelector('.wrapper')
      .querySelector('.products')
      .remove()

    this.title = undefined
    this.mediaDesktop = undefined
    this.mediaMobile = undefined
    this.products = undefined
    this.media_alt = undefined
    this.BISPopovers = {}
  }

  init(e) {
    this.title = e.title
    this.mediaDesktop = e.media_desktop
    this.mediaMobile = e.media_mobile
    this.products = document.querySelector(
      `#trunkshow-script--${e.index}`
    )?.innerText

    if (this.products) {
      this.products = JSON.parse(this.products)
    }
    this.mediaAlt = e.media_alt
  }

  async renderProduct(product) {
    let li = document.createElement('li')

    try {
      const res = await fetch(`${product}?sections=product-card`)
      if (!res.ok) throw new Error(res.status)

      const text = await res.json()
      const resultsMarkup = new DOMParser()
        .parseFromString(text['product-card'], 'text/html')
        .querySelector('product-card-wrapper')

      li.innerHTML = resultsMarkup.outerHTML
    } catch (error) {
      console.error('An error occurred in the predictive search request: ', {
        error,
      })
    }

    return li.outerHTML
  }

  async renderProducts(products) {
    if (!products) return
    let ul = document.createElement('ul')
    ul.classList.add('products')

    // Use Promise.all to ensure all products are rendered before appending, otherwise they might render out of order
    let liElements = await Promise.all(
      products.map(async (product) => {
        return await this.renderProduct(product.url)
      })
    )
    liElements = liElements.join('')
    ul.innerHTML = liElements

    this.shadowRoot
      .querySelector('.wrapper')
      .insertAdjacentElement('beforeend', ul)

    setTimeout(() => {
      this.initBISModal()
      const focusTrap = document
        .querySelector('#trunkshow-drawer')
        .shadowRoot.querySelector('hh-focus-trap')
      focusTrap.setAttribute('active', true)
      this.shadowRoot.querySelector('.wrapper')?.classList.add('fade-in')
    }, 0)
  }

  initBISModal() {
    const BISTriggerBtns = this.shadowRoot.querySelectorAll('.BIS_trigger')

    BISTriggerBtns.forEach((btn) =>
      btn.addEventListener('click', this.openBISModal.bind(this, btn))
    )
  }

  openBISModal(btn) {
    if (!BIS) return
    if (!this.BISPopovers[btn.dataset.productId]) {
      const product =
        btn.dataset.productData && JSON.parse(btn.dataset.productData)

      this.BISPopovers[btn.dataset.productId] = BIS.createPopover({
        productHandle: product.handle,
        product: product,
        button: {
          visible: false,
        },
      })
    }

    this.BISPopovers[btn.dataset.productId]?.form.show({
      variantId: parseFloat(btn.dataset.variantId),
    })
  }

  renderModalMedia() {
    return html`
      ${this.mediaDesktop?.media_type == 'video'
        ? html`
            <div style="height: 100%;">
              ${this.mediaMobile?.media_type == 'video' &&
              window.matchMedia('(max-width: 749px)').matches
                ? html`
                <video onloadedmetadata="this.muted = true"
                  playsinline="playsinline"
                  class="is-playing"
                  autoplay
                  loop
                  muted>
                    <source src="${findVideoSrc(
                      this.mediaMobile.sources
                    )}" type="video/mp4"></source>
                </video>`
                : html`
                  <video onloadedmetadata="this.muted = true"
                    playsinline="playsinline"
                    class="is-playing"
                    autoplay
                    loop
                    muted>
                      <source src="${findVideoSrc(
                        this.mediaDesktop.sources
                      )}" type="video/mp4"></source>
                  </video>
              `}
              <button
                class="thumbnail-play"
                aria-label="${t('accessibility.pause_video')}"
                @click="${handlePlay}"
              >
                ${unsafeHTML(paths.pause)} ${unsafeHTML(paths.play)}
              </button>
            </div>
          `
        : html`<picture>
            ${this.mediaMobile
              ? html`<source
                    srcset="${this.mediaMobile}"
                    media="(max-width: 749px)"
                  />
                  <source
                    srcset="${this.mediaDesktop}"
                    media="(min-width: 990px)"
                  /> `
              : html` <source srcset="${this.mediaDesktop}" />`}
            <img src="${this.mediaDesktop}" alt="${this.mediaAlt}" />
          </picture>`}
    `
  }

  handleCardClick(e) {
    const link = e
      .composedPath()[0]
      .closest('[data-featured-link]')
      ?.getAttribute('data-featured-link')
    if (link) {
      window.location.href = link
    }
  }

  render() {
    // the div that has `renderProducts()` always loads [Object Promise]
    // However, the function itself appends the products container, so it's okay
    // to just hide that div
    return html` <div class="wrapper js-wrapper" @click=${this.handleCardClick}>
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css"
      />

      <script
        src="https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js"
        defer
      ></script>

      <h1 class="title">${this.title}</h1>
      <div class="trunkshow-media media">${this.renderModalMedia()}</div>
      <div class="hidden">${this.renderProducts(this.products)}</div>
    </div>`
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .hidden {
      display: none;
    }

    .visually-hidden {
      position: absolute !important;
      overflow: hidden;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      clip: rect(0 0 0 0);
      word-wrap: normal !important;
    }

    *:focus-visible {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    .button.button--tertiary {
      background-color: transparent;
      border: none;
      cursor: pointer;
      display: block;
      padding: 0;
      text-decoration: none;
    }

    .swatch-colors li a {
      color: var(--hh-color-ink);
      display: block;
      text-decoration: none;
    }

    .wrapper {
      display: grid;
      grid-template-areas: 'title' 'media' 'products';
      opacity: 0;
      transition: opacity var(--hh-duration-default) 200ms;
    }

    .fade-in {
      opacity: 1;
    }

    @media screen and (min-width: 990px) {
      .wrapper {
        grid-template-columns: 42% 1fr;
        grid-template-areas: 'media title' 'media products';
        grid-template-rows: auto 1fr;
        max-height: 100dvh;
      }
    }

    .title {
      grid-area: title;
      font: var(--hh-h5-font);
      letter-spacing: var(--hh-h5-letter-spacing);
      margin: 0;
      padding: var(--hh-space-sm) var(--hh-space-page-margins);
      text-transform: var(--hh-h5-text-transform);
    }

    @media screen and (min-width: 990px) {
      .title {
        font: var(--hh-h4-font);
        padding: 4rem 9rem 2rem 7rem;
      }
    }

    .media {
      grid-area: media;
    }

    @media screen and (min-width: 990px) {
      .media {
        min-height: 100dvh;
      }
    }

    .media img {
      display: block;
      width: 100%;
    }

    @media screen and (min-width: 990px) {
      .media img {
        height: 100%;
        object-fit: cover;
      }

      .trunkshow-media img {
        object-position: top;
      }
    }

    .media video {
      display: block;
      height: 100%;
      max-width: 100%;
      object-fit: cover;
    }

    .products {
      align-items: flex-start;
      grid-area: products;
      display: grid;
      gap: 8px;
      grid-template-columns: 1fr 1fr;
      list-style: none;
      margin: 0;
      overflow-y: auto;
      padding: 12px 16px;
    }

    @media screen and (min-width: 990px) {
      .products {
        gap: var(--hh-page-gutter);
        padding: 0 9rem 2rem 7rem;
      }
    }

    .product-card__media {
      cursor: pointer;
      position: relative;
      --swiper-theme-color: var(--hh-color-neutral);
      --swiper-navigation-size: 16px;
      --swiper-scrollbar-drag-bg-color: var(--hh-color-neutral);
      --swiper-scrollbar-border-radius: 0;
      --swiper-scrollbar-bg-color: transparent;
      --swiper-scrollbar-sides-offset: 0%;
      --swiper-scrollbar-bottom: 0px;
    }

    .product-card__media .swiper-container,
    .product-card__media div[role='region'] {
      height: 100%;
    }

    .product-media {
      height: 100%;
      max-width: 100%;
      transition: opacity var(--hh-duration-default);
      display: block;
    }

    product-card-wrapper .product-card__media img {
      max-width: 50vw;
    }

    @media screen and (min-width: 990px) {
      product-card-wrapper .product-card__media img {
        max-width: 25vw;
      }
    }

    .product-card__media .swiper-wrapper {
      width: fit-content;
    }

    .product-media--secondary {
      left: 0;
      opacity: 0;
      position: absolute;
      top: 0;
    }

    hh-product-form .swiper-button-prev,
    hh-product-form .swiper-button-next {
      opacity: 0;
      padding: 10px;
    }

    .product-card__media .swiper-button-next:focus-visible,
    .product-card__media .swiper-button-prev:focus-visible {
      opacity: 1;
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    .product-card__media .swiper-button-next,
    .product-card__media .swiper-button-prev {
      color: var(--swiper-navigation-color, var(--hh-color-gray-2));
    }

    .skip-to-content-link {
      color: inherit;
      text-decoration: none;
    }

    .skip-to-content-link:focus-visible {
      z-index: 9999;
      position: inherit;
      overflow: auto;
      width: auto;
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
      height: auto;
      clip: auto;
    }

    @media (hover: hover) {
      .product-card__media:hover .product-media--secondary,
      .product-card__media:hover .swiper-button-prev,
      .product-card__media:hover .swiper-button-next {
        opacity: 1;
        z-index: 2;
      }

      product-card-wrapper:hover .product-card__swatches {
        opacity: 1;
        overflow: hidden;
      }

      product-card-wrapper:hover .product-card__bottom .price {
        opacity: 0;
        visibility: hidden;
      }
    }

    .product-card__info-wrapper {
      position: relative;
    }

    .product-card__info {
      display: flex;
      flex-direction: column;
    }

    hh-product-card-variants {
      display: grid;
      gap: var(--hh-space-xs);
      grid-template-rows: auto 1fr;
    }

    product-card-wrapper .product-card__media,
    product-card-wrapper .swiper {
      aspect-ratio: 328 / 442;
    }

    product-card-wrapper .product-card__media {
      width: 100%;
      transition: opacity ease var(--hh-duration-default);
    }

    product-card-wrapper.fade-out .product-card__media {
      opacity: 0;
    }

    product-card-wrapper .product-card__media img {
      width: 100%;
      aspect-ratio: 328 / 442;
      object-fit: cover;
      max-height: 100%;
      height: 100%;
    }

    .product-card__swatches {
      display: none;
    }

    @media screen and (min-width: 990px) {
      .product-card__swatches {
        align-items: flex-start;
        background-color: var(--hh-color-reverse);
        display: flex;
        gap: var(--hh-space-xs);
        height: auto;
        list-style: none;
        max-width: 100%;
        overflow: auto;
        opacity: 0;
        padding: 0;
        position: absolute;
        top: 0;
        transition: opacity var(--hh-duration-default);
        width: 100%;
      }
    }

    .product-card__swatches:focus-within {
      opacity: 1 !important;
    }

    product-card-wrapper:focus-within .product-card__swatches {
      opacity: 1;
    }

    .product-card__bottom {
      gap: 8px;
      display: flex;
      padding-block: 1px;
      flex-direction: column;
    }

    @media screen and (min-width: 750px) {
      .product-card__bottom {
        align-items: center;
        flex-direction: row;
        justify-content: space-between;
      }
    }

    .swatch-size-button {
      background-color: transparent;
      border: 0;
      cursor: pointer;
      margin: 0;
      padding: 0;
    }

    .swatch-size-button--disabled {
      color: var(--hh-color-subdued);
    }

    .swatch-size-button:not(.swatch-size-button--disabled) {
      font-weight: bold;
    }

    .swatch-color-button .disabled-line {
      height: 1px;
      position: relative;
      top: 0;
      left: 0;
      transform: rotate(130deg);
      display: block;
      pointer-events: none;
      background-color: var(--hh-color-ink, 0.5);
    }

    .product-card__media .swiper {
      z-index: 3 !important;
    }

    .product-card__media .swiper-button-prev,
    .product-card__media .swiper-button-next {
      z-index: 3 !important;
    }

    wishlist-button-collection {
      display: block;
      position: relative;
      width: 100%;
    }
    .c-subdued {
      color: var(--hh-color-subdued);
    }

    .badge {
      color: var(--hh-color-gray-2);
      font: var(--hh-utility-4);
      left: 10px;
      position: absolute;
      text-transform: uppercase;
      top: 10px;
      z-index: 3;
    }

    .relative {
      position: relative;
    }

    .swiper-scrollbar-drag:empty {
      display: block;
    }

    /* The following two classes are in reference to this issue (https://github.com/nolimits4web/swiper/issues/3599), where the slider initializes much too wide when within a flex/grid item */
    .swiper-container {
      display: grid;
    }

    :root {
      --swiper-theme-color: var(--hh-color-subdued) !important;
      --swiper-navigation-size: 16px !important;
      --hh-announcement-height: 42px;
      --hh-header-height: 60px;
    }

    .swiper {
      height: 100%;
      max-width: 100%;
    }

    .price {
      align-items: center;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }

    .price--align-left {
      align-items: flex-start;
    }

    .price dl {
      margin: 0;
      display: flex;
      flex-direction: column;
    }

    .price--sold-out .price__availability,
    .price__regular {
      display: block;
    }

    .price__sale,
    .price__availability,
    .price--on-sale .price__regular,
    .price--on-sale .price__availability,
    .price--no-compare .price__compare {
      display: none;
    }

    .price--on-sale .price__sale {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }

    .price--on-sale .price-item--regular {
      text-decoration: line-through;
    }

    .price__sale .price-item--sale {
      margin-left: var(--hh-space-2xs);
    }

    .visually-hidden {
      position: absolute !important;
      overflow: hidden;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      clip: rect(0 0 0 0);
      word-wrap: normal !important;
    }

    dd {
      margin: 0;
    }

    .body-2 {
      font: var(--hh-body-2-font);
      letter-spacing: var(--hh-body-2-letter-spacing);
      margin: 0;
      text-transform: var(--hh-body-text-transform);
    }

    .capitalize {
      text-transform: capitalize;
    }

    .thumbnail-play {
      background: transparent;
      border: none;
      bottom: var(--hh-space-sm);
      cursor: pointer;
      display: flex;
      padding: 0;
      position: absolute;
      right: var(--hh-space-sm);
      z-index: 1;
    }

    video:not(.is-playing) + .thumbnail-play .icon-pause {
      display: none;
    }

    video:not(.is-playing) + .thumbnail-play .icon-play {
      display: block;
    }

    video.is-playing + .thumbnail-play .icon-play {
      display: none;
    }

    .flex {
      display: flex;
    }
    .jcb {
      justify-content: space-between;
    }
    .aic {
      align-items: center;
    }

    .swatch-colors {
      align-items: center;
      display: flex;
      gap: var(--hh-space-xs);
      list-style-type: none;
      padding-left: 0;
      padding-right: 1rem;
      z-index: 1;
    }

    .swatch-color-button {
      width: 12px;
      display: block;
      height: 12px;
      border-radius: 50%;
      padding: 0;
      border: 1px solid var(--hh-color-subdued);
      box-sizing: content-box;
    }

    .swatch-color-button img {
      border-radius: 50%;
      display: block;
      width: 12px;
    }

    .swatch-color-button.active {
      outline: 1px solid var(--hh-color-ink);
      outline-offset: 2px;
    }

    product-card-wrapper {
      position: relative;
    }

    .wk-button {
      background-color: transparent;
      border: none;
      cursor: pointer;
      height: 48px;
      padding: 0;
      position: absolute;
      right: 0;
      top: 0;
      width: 48px;
      z-index: 4;
    }

    wk-icon {
      display: block;
      width: 100%;
    }

    wk-icon svg {
      width: 18px;
    }

    wk-icon:hover svg {
      stroke: var(--hh-color-ink);
    }

    .wk-selected svg {
      stroke: var(--hh-color-ink);
      fill: var(--hh-color-ink);
    }
  `
}

customElements.define('hh-trunkshow', Trunkshow)
